import { registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export interface LargeFileInfo {
  uri: string;
  name?: string;
  displayName?: string;
  size?: number;
  sizeFormatted?: string;
  mimeType?: string;
  extension?: string;
  isCloudFile?: boolean;
  provider?: string;
}

export interface MultipleLargeFilesResult {
  files: LargeFileInfo[];
}

export interface LargeFileUploaderPlugin {
  pickFile(params: {
    mimeType?: string;
    allowMultiple?: boolean;
  }): Promise<LargeFileInfo | MultipleLargeFilesResult>;
  uploadFile(options: {
    fileUrl: string;
    authToken: string;
    serverUrl: string;
  }): Promise<void>;
  cancel(): Promise<void>;
  addListener(
    eventName: "onProgress",
    listenerFunc: (data: { progress: number }) => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "onComplete",
    listenerFunc: (data: { downloadedFileUrl: string }) => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "onCancel",
    listenerFunc: () => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "onError",
    listenerFunc: (data: { error: string; isFatal?: boolean }) => void
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "onRetry",
    listenerFunc: (data: {
      attempt: number;
      maxAttempts: number;
      delaySeconds: number;
      isWaitingForNetwork: boolean;
    }) => void
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

export const largeFileUploader =
  registerPlugin<LargeFileUploaderPlugin>("LargeFileUploader");

export type LargeFileUploadHandlers = {
  onProgress: (data: { progress: number }) => void;
  onComplete: (data: { downloadedFileUrl: string }) => void;
  onCancel: () => void;
  onError: (data: { error: string; isFatal?: boolean }) => void;
  onRetry: (data: {
    attempt: number;
    maxAttempts: number;
    delaySeconds: number;
    isWaitingForNetwork: boolean;
  }) => void;
};

/**
 * Attach the complete native upload event protocol as one capability operation.
 * Construction of the client stays inert; cleanup is idempotent and also
 * removes listeners whose Capacitor registration resolves after unmount.
 */
export const attachLargeFileUploadListeners = (
  handlers: LargeFileUploadHandlers,
  plugin: LargeFileUploaderPlugin = largeFileUploader
): (() => Promise<void>) => {
  const listeners = [
    // action-id: lifecycle.upload.progress
    plugin.addListener("onProgress", handlers.onProgress),
    // action-id: lifecycle.upload.completed
    plugin.addListener("onComplete", handlers.onComplete),
    // action-id: lifecycle.upload.cancelled
    plugin.addListener("onCancel", handlers.onCancel),
    // action-id: lifecycle.upload.failed
    plugin.addListener("onError", handlers.onError),
    // action-id: lifecycle.upload.retrying
    plugin.addListener("onRetry", handlers.onRetry),
  ];
  let cleanup: Promise<void> | undefined;

  return () => {
    cleanup ||= Promise.allSettled(
      listeners.map(async (listener) => (await listener).remove())
    ).then(() => undefined);
    return cleanup;
  };
};
